// Stage 46: WebGL fragment-shader wallpaper for the LargeDotWidget.
// Ports supershapeDOT/DOT-v4-large.html into a panel-scoped React
// component. Two GLSL programs (liquid, flow), 12 ZeroPersona
// mood palettes, audio uniforms from the speech bus.

import { useEffect, useRef } from 'react';
import {
  paletteForMood,
  buildMonoPalette,
  WALLPAPER_PALETTE_FALLBACK,
  type WallpaperPalette,
} from '../constants/moodWallpaperPalettes';

export type WallpaperMode = 'liquid' | 'flow' | 'static';

interface LargeDotWallpaperProps {
  /** Selected program: liquid → 0, flow → 1. static keeps the
   *  most-recently-bound program but freezes time. */
  mode: WallpaperMode;
  /** Lowercased ZeroPersona mood label, or null. */
  moodLabel: string | null;
  /** When false, palette is built from `color` (monochrome). */
  moodBackgroundEnabled: boolean;
  /** dot_color hex. Used for the monochrome fallback palette. */
  color: string;
  /** Shared speech-bus analyser. Driven via prop so the same
   *  AnalyserNode the supershape uses also drives the wallpaper. */
  analyser: AnalyserNode | null;
}

const VERT_SRC = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

// Both fragment shaders are verbatim transcriptions from the
// reference template (frag-liquid / frag-flow). Do not edit them
// without copying into a shader playground first — the noise
// constants (43758.5453, the FBM amplitudes / lacunarity) are
// load-bearing for the field's appearance.
const FRAG_LIQUID = `
precision highp float;
uniform vec2  u_res;
uniform float u_t;
uniform float u_audio;
uniform float u_bass;
uniform float u_mid;
uniform float u_high;
uniform float u_pulse;
uniform vec3  u_c0;
uniform vec3  u_c1;
uniform vec3  u_c2;
uniform vec3  u_c3;

float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  float a = hash(i), b = hash(i+vec2(1,0));
  float c = hash(i+vec2(0,1)), d = hash(i+vec2(1,1));
  vec2 u = f*f*(3.0-2.0*f);
  return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
}
float fbm(vec2 p){
  float s = 0.0, a = 0.5;
  for(int i=0;i<5;i++){ s += a*vnoise(p); p *= 2.02; a *= 0.5; }
  return s;
}
void main(){
  vec2 p = (gl_FragCoord.xy - 0.5*u_res.xy) / u_res.y;
  float ang = u_t * 0.06 + u_bass * 0.18;
  float ca = cos(ang), sa = sin(ang);
  vec2 pr2 = vec2(ca*p.x - sa*p.y, sa*p.x + ca*p.y);
  float breath = 1.0 - 0.10*u_bass;
  vec2 q0 = pr2 * breath;
  float t = u_t * 0.12;
  vec2 q = q0;
  q += 0.30 * vec2(fbm(q0*1.4 + t), fbm(q0*1.4 - t + 7.0));
  float r = length(p);
  float ring = u_pulse * exp(-12.0*abs(r - (1.0-u_pulse)*0.9)) * 0.6;
  float n = fbm(q*1.6 + vec2(0.0, t*2.0));
  n += 0.5*fbm(q*3.3 - t*1.3);
  n += ring + u_high*0.10 + u_mid*0.05;
  vec3 col = mix(u_c0, u_c1, smoothstep(0.15, 0.55, n));
  col = mix(col, u_c2, smoothstep(0.55, 0.78, n));
  col = mix(col, u_c3, smoothstep(0.82, 0.95, n));
  col *= smoothstep(1.5, 0.4, length(p));
  col += (hash(gl_FragCoord.xy + u_t)-0.5) * 0.02;
  col *= 0.88;
  gl_FragColor = vec4(col, 1.0);
}
`;

const FRAG_FLOW = `
precision highp float;
uniform vec2  u_res;
uniform float u_t;
uniform float u_audio;
uniform float u_bass;
uniform float u_mid;
uniform float u_high;
uniform float u_pulse;
uniform vec3  u_c0;
uniform vec3  u_c1;
uniform vec3  u_c2;
uniform vec3  u_c3;

float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  float a = hash(i), b = hash(i+vec2(1,0));
  float c = hash(i+vec2(0,1)), d = hash(i+vec2(1,1));
  vec2 u = f*f*(3.0-2.0*f);
  return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
}
float fbm(vec2 p){
  float s=0.0, a=0.5;
  for(int i=0;i<4;i++){ s += a*vnoise(p); p*=2.03; a*=0.5; }
  return s;
}
void main(){
  vec2 p = (gl_FragCoord.xy - 0.5*u_res.xy) / u_res.y;
  float ang = u_t * 0.05 + u_bass * 0.15;
  float ca = cos(ang), sa = sin(ang);
  vec2 pr2 = vec2(ca*p.x - sa*p.y, sa*p.x + ca*p.y);
  float breath = 1.0 + 0.06*u_mid - 0.05*u_bass;
  vec2 q0 = pr2 * breath;
  vec2 q = q0;
  float acc = 0.0;
  float w = 1.0;
  float innerAng = -u_t * 0.03;
  float ica = cos(innerAng), isa = sin(innerAng);
  for(int i=0;i<14;i++){
    float n = fbm(q*1.7 + u_t*0.10);
    float a = n * 6.2831 + u_t*0.18;
    vec2 dir = vec2(cos(a), sin(a));
    vec2 d = q;
    float r = length(d) + 0.001;
    vec2 swirl = vec2(-d.y, d.x)/r * 0.18 * exp(-r*1.4);
    dir += swirl;
    dir = vec2(ica*dir.x - isa*dir.y, isa*dir.x + ica*dir.y);
    q += dir * 0.012;
    acc += w * (0.5 + 0.5*sin(n*12.0 + u_t*1.2));
    w *= 0.86;
  }
  acc /= 6.0;
  acc += u_high*0.05;
  float pr = length(p);
  float ring = u_pulse * exp(-25.0*abs(pr - (1.0-u_pulse)*1.0));
  vec3 col = mix(u_c0, u_c1, smoothstep(0.2, 0.55, acc));
  col = mix(col, u_c2, smoothstep(0.55, 0.8, acc));
  col = mix(col, u_c3, smoothstep(0.85, 1.0, acc));
  col += ring * (u_c2 * 1.1 + u_c3 * 0.4) * 0.5;
  col *= smoothstep(1.55, 0.3, length(p));
  col *= 0.85;
  gl_FragColor = vec4(col, 1.0);
}
`;

interface ProgramHandle {
  prog: WebGLProgram;
  a_pos: number;
  u_res: WebGLUniformLocation | null;
  u_t: WebGLUniformLocation | null;
  u_audio: WebGLUniformLocation | null;
  u_bass: WebGLUniformLocation | null;
  u_mid: WebGLUniformLocation | null;
  u_high: WebGLUniformLocation | null;
  u_pulse: WebGLUniformLocation | null;
  u_c0: WebGLUniformLocation | null;
  u_c1: WebGLUniformLocation | null;
  u_c2: WebGLUniformLocation | null;
  u_c3: WebGLUniformLocation | null;
}

function compile(
  gl: WebGLRenderingContext,
  type: number,
  src: string,
): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[wallpaper] shader compile failed:',
      gl.getShaderInfoLog(s),
    );
    gl.deleteShader(s);
    return null;
  }
  return s;
}

function buildProgram(
  gl: WebGLRenderingContext,
  fragSrc: string,
): ProgramHandle | null {
  const vs = compile(gl, gl.VERTEX_SHADER, VERT_SRC);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fragSrc);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    // eslint-disable-next-line no-console
    console.warn('[wallpaper] link failed:', gl.getProgramInfoLog(prog));
    gl.deleteProgram(prog);
    return null;
  }
  return {
    prog,
    a_pos: gl.getAttribLocation(prog, 'a_pos'),
    u_res: gl.getUniformLocation(prog, 'u_res'),
    u_t: gl.getUniformLocation(prog, 'u_t'),
    u_audio: gl.getUniformLocation(prog, 'u_audio'),
    u_bass: gl.getUniformLocation(prog, 'u_bass'),
    u_mid: gl.getUniformLocation(prog, 'u_mid'),
    u_high: gl.getUniformLocation(prog, 'u_high'),
    u_pulse: gl.getUniformLocation(prog, 'u_pulse'),
    u_c0: gl.getUniformLocation(prog, 'u_c0'),
    u_c1: gl.getUniformLocation(prog, 'u_c1'),
    u_c2: gl.getUniformLocation(prog, 'u_c2'),
    u_c3: gl.getUniformLocation(prog, 'u_c3'),
  };
}

function clonePalette(p: WallpaperPalette): number[][] {
  return [
    [p[0][0], p[0][1], p[0][2]],
    [p[1][0], p[1][1], p[1][2]],
    [p[2][0], p[2][1], p[2][2]],
    [p[3][0], p[3][1], p[3][2]],
  ];
}

export function LargeDotWallpaper(
  props: LargeDotWallpaperProps,
): JSX.Element {
  const { mode, moodLabel, moodBackgroundEnabled, color, analyser } =
    props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Refs that the raf loop reads without re-running the effect.
  const modeRef = useRef<WallpaperMode>(mode);
  const moodLabelRef = useRef<string | null>(moodLabel);
  const moodBgRef = useRef<boolean>(moodBackgroundEnabled);
  const colorRef = useRef<string>(color);
  const analyserRef = useRef<AnalyserNode | null>(analyser);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  useEffect(() => {
    moodLabelRef.current = moodLabel;
  }, [moodLabel]);
  useEffect(() => {
    moodBgRef.current = moodBackgroundEnabled;
  }, [moodBackgroundEnabled]);
  useEffect(() => {
    colorRef.current = color;
  }, [color]);
  useEffect(() => {
    analyserRef.current = analyser;
  }, [analyser]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', {
      antialias: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
    }) as WebGLRenderingContext | null;
    if (!gl) {
      canvas.style.display = 'none';
      return;
    }

    const liquid = buildProgram(gl, FRAG_LIQUID);
    const flow = buildProgram(gl, FRAG_FLOW);
    if (!liquid || !flow) {
      canvas.style.display = 'none';
      return;
    }

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    // Eased palette state. Match reference k=0.06 per frame.
    const palette = clonePalette(WALLPAPER_PALETTE_FALLBACK);
    let paletteTarget = clonePalette(WALLPAPER_PALETTE_FALLBACK);

    // Mode tracking. lastBoundIndex remembers which program was
    // active when 'static' was first set so we keep rendering it.
    let lastBoundIndex = 0; // 0=liquid, 1=flow
    let frozenT: number | null = null;

    const t0 = performance.now();
    let pulse = 0;
    let raf = 0;

    const reducedMotionMq = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, canvas.clientWidth);
      const h = Math.max(1, canvas.clientHeight);
      const pw = Math.floor(w * dpr);
      const ph = Math.floor(h * dpr);
      if (canvas.width !== pw) canvas.width = pw;
      if (canvas.height !== ph) canvas.height = ph;
      gl.viewport(0, 0, pw, ph);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const sampleAudio = (): {
      peak: number;
      bass: number;
      mid: number;
      high: number;
    } => {
      const a = analyserRef.current;
      if (!a) return { peak: 0, bass: 0, mid: 0, high: 0 };
      const buf2 = new Uint8Array(a.frequencyBinCount);
      a.getByteFrequencyData(buf2);
      const sampleRate = a.context.sampleRate;
      const binHz = sampleRate / a.fftSize;
      const lo = (hz: number): number =>
        Math.max(0, Math.floor(hz / binHz));
      const hi = (hz: number): number =>
        Math.min(buf2.length - 1, Math.ceil(hz / binHz));
      const avg = (loHz: number, hiHz: number): number => {
        const l = lo(loHz);
        const h = hi(hiHz);
        let sum = 0;
        let n = 0;
        for (let i = l; i <= h; i++) {
          sum += buf2[i];
          n++;
        }
        return n > 0 ? sum / n / 255 : 0;
      };
      const bass = avg(20, 250);
      const mid = avg(250, 4000);
      const high = avg(4000, 16000);
      const peak = Math.max(bass, mid, high);
      return { peak, bass, mid, high };
    };

    const frame = (): void => {
      raf = requestAnimationFrame(frame);
      if (document.hidden) return;

      const now = performance.now();
      const tWall = (now - t0) / 1000;

      // Resolve target palette this frame.
      const monoOn = !moodBgRef.current;
      const target = monoOn
        ? buildMonoPalette(colorRef.current)
        : paletteForMood(moodLabelRef.current);
      paletteTarget = clonePalette(target);

      const k = 0.06;
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 3; j++) {
          palette[i][j] += (paletteTarget[i][j] - palette[i][j]) * k;
        }
      }

      const m = modeRef.current;
      let idx = lastBoundIndex;
      if (m === 'liquid') idx = 0;
      else if (m === 'flow') idx = 1;
      lastBoundIndex = idx;

      const isStatic = m === 'static' || reducedMotionMq.matches;
      if (isStatic) {
        if (frozenT === null) frozenT = tWall;
      } else {
        frozenT = null;
      }
      const t = isStatic ? (frozenT ?? tWall) : tWall;

      const audio = isStatic
        ? { peak: 0, bass: 0, mid: 0, high: 0 }
        : sampleAudio();

      pulse *= 0.95;
      if (pulse < 0.001) pulse = 0;

      const P = idx === 0 ? liquid : flow;
      gl.useProgram(P.prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.enableVertexAttribArray(P.a_pos);
      gl.vertexAttribPointer(P.a_pos, 2, gl.FLOAT, false, 0, 0);

      gl.uniform2f(P.u_res, canvas.width, canvas.height);
      gl.uniform1f(P.u_t, t);
      gl.uniform1f(P.u_audio, audio.peak);
      gl.uniform1f(P.u_bass, audio.bass);
      gl.uniform1f(P.u_mid, audio.mid);
      gl.uniform1f(P.u_high, audio.high);
      gl.uniform1f(P.u_pulse, pulse);
      gl.uniform3f(P.u_c0, palette[0][0], palette[0][1], palette[0][2]);
      gl.uniform3f(P.u_c1, palette[1][0], palette[1][1], palette[1][2]);
      gl.uniform3f(P.u_c2, palette[2][0], palette[2][1], palette[2][2]);
      gl.uniform3f(P.u_c3, palette[3][0], palette[3][1], palette[3][2]);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      try {
        gl.deleteProgram(liquid.prog);
        gl.deleteProgram(flow.prog);
        if (buf) gl.deleteBuffer(buf);
      } catch {
        /* ignore — context may already be lost on unmount */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="large-dot-bg"
      aria-hidden="true"
    />
  );
}
